import Link from 'next/link';
import { useRouter } from 'next/router';

import {
  useMediaQuery,
  Avatar,
  Typography,
  Grid,
  Divider,
  CardActionArea,
  Pagination,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { FormattedDate } from 'react-intl';

import { Image } from '@/src/components/atoms';
import { CardProduct, EmptyState } from '@/src/components/organisms';

const useStyles = makeStyles(theme => ({
  cardProduct: {
    display: 'flex',
    height: '100%',
    borderRadius: theme.spacing(1),
    position: 'relative',
    '& .card-product__media': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  cardProductReverse: {
    '& .case-content': {
      alignSelf: 'flex-end',
    },
  },
  imageContainer: {
    width: '100%',
    height: '100%',
  },
  image: {
    objectFit: 'cover',
  },
  imageCover: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'black',
    opacity: 0.3,
  },
  caseContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    zIndex: 3,
    maxWidth: '50%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  list: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    marginRight: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  tag: {
    padding: theme.spacing(1 / 2, 1),
    borderRadius: theme.spacing(1 / 2),
    background: theme.palette.secondary.light,
    color: 'white',
    margin: theme.spacing(0, 1, 1, 0),
    cursor: 'pointer',
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 2, 2, 0),
    },
  },
  textWhite: {
    color: 'white',
  },
}));

const Results = ({ data, className, ...rest }) => {
  const router = useRouter();
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const { search = '' } = router.query;
  const { results = [], page: currentPage = 1, total_pages: totalPages = 1 } = data;

  const handlePageChange = (_, value) =>
    router.push({ pathname: '', query: { search, page: value } }, undefined, {
      shallow: true,
      scroll: true,
    });

  const CaseMediaContent = ({ src, alt }) => (
    <div className={classes.imageContainer}>
      <Image
        src={src}
        alt={alt}
        className={classes.image}
        lazyProps={{ width: '100%', height: '100%' }}
      />
      <div className={classes.imageCover} />
    </div>
  );

  const CaseContent = content => (
    <div className={clsx(classes.caseContent, 'case-content')}>
      <Typography variant="h5" gutterBottom className={classes.textWhite}>
        {content.title}
      </Typography>
      <Typography variant="subtitle1" gutterBottom className={classes.textWhite}>
        {content.subtitle}
      </Typography>
      <div className={classes.tags}>
        {content.tags.map((item, index) => (
          <Typography
            variant="caption"
            color="primary"
            className={classes.tag}
            key={index.toString()}>
            {item}
          </Typography>
        ))}
      </div>
      <div style={{ flexGrow: 1 }} />
      <Divider className={classes.divider} />
      <div className={classes.list}>
        <div className={classes.avatarContainer}>
          <Avatar src={content.author?.avatar?.url} className={classes.avatar} />
          <Typography variant="body2" className={classes.textWhite}>
            {content.author?.name}
          </Typography>
        </div>
        <Typography variant="overline" className={classes.textWhite}>
          <FormattedDate value={content.date} />
        </Typography>
      </div>
    </div>
  );

  if (results.length === 0) {
    return (
      <div className={className} {...rest}>
        <EmptyState
          subtitle={
            router.query.search
              ? `Nenhum caso de sucesso com o termo '${router.query.search}'`
              : 'Nenhum caso de sucesso encontrado para mostrar agora'
          }
          ButtonProps={{ onClick: () => router.back() }}
        />
      </div>
    );
  }

  return (
    <div className={className} {...rest}>
      <Grid container spacing={isMd ? 4 : 2}>
        {results?.map((item, index) => (
          <Grid item xs={12} key={index.toString()} data-aos="fade-up">
            <Link href={['/success-cases/article', item.uid].join('/')} passHref>
              <CardActionArea component="a">
                <CardProduct
                  withShadow
                  liftUp
                  className={clsx(
                    classes.cardProduct,
                    index % 2 !== 0 ? classes.cardProductReverse : {},
                  )}
                  mediaContent={
                    <CaseMediaContent src={item.data.cover.url} alt={item.data.title} />
                  }
                  cardContent={
                    <CaseContent
                      title={item.data.title}
                      subtitle={item.data.subtitle}
                      tags={item.tags}
                      author={item.data.author.data}
                      date={item.data.date}
                    />
                  }
                />
              </CardActionArea>
            </Link>
          </Grid>
        ))}
        {totalPages > 1 && (
          <Grid item container xs={12} data-aos="fade-up" justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              color="primary"
              size="large"
              shape="rounded"
              onChange={handlePageChange}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

Results.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * data to be rendered
   */
  data: PropTypes.object,
};

export default Results;
